<template>
    <div class="setting-content--section">
        <div v-if="state === 'create' || state === 'needs-data'">
            <p>{{ topMessage }} <a target="_blank" href="https://help.getgekko.com/help/ideal-betaallinks">FAQ</a>.</p>
        </div>
        <mollie-payments-left v-else></mollie-payments-left>
        <div class="process-box__container">
            <div class="process-box">
                <div class="process-box__header">
                    <div class="process-box__logo">
                        <img src="/static/img/third-party/mollie_logo_dark.svg" alt="mollie">
                    </div>
                    <span class="process-box__title"></span>
                    <span v-if="!isActiveState" class="process-box__status process-box__status_inactive"
                        v-b-tooltip.hover
                        :title="$gettext('Your connection to Mollie is inactive. Follow the steps below to setup it up.')">
                        {{ $gettext("Inactive") }}
                    </span>
                    <span v-if="isActiveState" class="process-box__status process-box__status_active" v-b-tooltip.hover
                        :title="$gettext('Your connection to Mollie is set up. You can now send iDEAL links with your invoices.')">
                        {{ $gettext("Active") }}
                    </span>
                    <div v-if="state !== 'create'">
                        <b-dropdown v-if="state !== 'create'" size="lg" variant="link" right
                            toggle-class="text-decoration-none" aria-expanded="false" no-caret
                            class="ui-dashboard-menu-dropdown dropdown">
                            <template #button-content>
                                <font-awesome-icon icon="fa-solid fa-ellipsis-vertical" size="l" />
                            </template>
                            <b-dropdown-item href="" v-b-modal.modal>
                                <span class="process-box__disconnect-btn">
                                    <font-awesome-icon icon="fa-solid fa-trash-can" class="text-danger" size="m" />
                                    <span class="text-danger">{{ $gettext("Disconnect") }}</span>
                                </span>
                            </b-dropdown-item>
                        </b-dropdown>
                        <b-modal @ok="disconnect()" id="modal" :title="$gettext('Disconnect')">
                            <p>{{ disconnectMessage }}</p>
                        </b-modal>
                    </div>
                </div>
                <div class="process-box__content">
                    <div v-if="state === 'create' || state === 'needs-data'" class="stepper">
                        <div class="stepper__step">
                            <div class="stepper__details">
                                <div class="timeline timeline_first">
                                    <div class="timeline__symbol">
                                        <div class="timeline__number">1</div>
                                    </div>
                                    <div class="timeline__vline"></div>
                                </div>
                                <div class="stepper__text stepper__text_first">
                                    <div class="stepper__header">{{ $gettext("Connect your Mollie account to Gekko") }}
                                    </div>
                                    <div class="stepper__subheader" v-if="state !== 'needs-data'"></div>
                                    <div class="stepper__subheader" v-if="state === 'needs-data'">
                                        {{ $gettext("Your Mollie account is not ready to use. Please go to Mollie and complete your profile.") }}
                                    </div>
                                </div>
                            </div>
                            <div class="stepper__action stepper__action_first">
                                <GekkoButton :text="$gettext('Get started')" type="button" @click="connectMollie"
                                    v-if="state !== 'needs-data'" />
                                <div v-if="state === 'needs-data'" class="stepper__done">
                                    <a analytics data-analytics-click="Mollie: fill in data"
                                        href="https://www.mollie.com/dashboard/onboarding/organization-details"
                                        class="btn btn-primary">{{ $gettext("Complete Profile") }}</a>
                                </div>
                            </div>
                        </div>
                        <div class="stepper__step stepper__step--grayedout">
                            <div class="stepper__details">
                                <div class="timeline">
                                    <div class="timeline__symbol">
                                        <div class="timeline__number">2</div>
                                    </div>
                                </div>
                                <div class="stepper__text">
                                    <div class="stepper__header ">{{ $gettext("Ready to send iDEAL links") }}</div>
                                </div>
                            </div>
                            <div class="stepper__action">
                                <font-awesome-icon icon="fa-solid fa-check" size="xl" />
                            </div>
                        </div>
                    </div>
                    <div v-if="state === 'verified' || state === 'in-review'" class="process-box__info">
                        <div class="process-box__status-line">
                            <label class="process-box__label">{{ $gettext("Mollie status:") }} </label>
                            <span class="process-box__value">
                                <span v-if="state !== 'in-review'">{{ $gettext("Verified") }}</span>
                                <span v-if="state === 'in-review'" id="tooltip-target-review">{{ $gettext("In review") }}
                                    <font-awesome-icon id="tt_mollie_review" icon="fa-solid fa-circle-info"
                                        class="text-info" size="m" />
                                    <b-tooltip target="tt_mollie_review" triggers="hover">
                                        {{ $gettext('You can now send invoices with iDEAL links. The money that you receive will be stored in your Mollie account until the information that you submitted will be reviewed.This could take a few days.') }}
                                    </b-tooltip>
                                </span>
                            </span>
                        </div>
                        <div class="process-box__account-line">
                            <span class="process-box__label">{{ $gettext("Website profile:") }}</span>
                            <span v-if="allProfiles.length > 1" class="process-box__value">
                                <b-form-select v-model="chosen_profile" :options="allProfiles"
                                               text-field="name"
                                               value-field="id"/>
                            </span>
                            <span v-if="allProfiles.length === 1" class="process-box__value">
                                {{ mollieUser.name }}
                            </span>
                            <span v-if="allProfiles.length === 0" class="process-box__value warning">
                                {{ $gettext("No profiles found. Please create a website profile on your Mollie account.") }}
                            </span>
                            <GekkoButton :text="$gettext('Update')" type="button"
                                v-if="chosen_profile !== mollieUser.id" @click="changeProfile()" />
                        </div>
                        <div class="process-box__account-line">
                            <span class="process-box__label">{{ $gettext("Mollie account:") }}</span>
                            <span class="process-box__value">{{ mollieUser.email }}</span>
                        </div>
                        <div class="process-box__attach-line">
                            <label class="process-box__label" for="checkbox">{{ $gettext("Include iDEAL payment links to invoice emails: ") }}</label>
                            <b-form-checkbox id="checkbox" v-model="attach_mollie_payment" class="process-box__value"
                                @change="updateMollieOnCompanySettings" />
                        </div>
                    </div>
                    <div v-if="state === 'blocked'" class="stepper">
                        <div class="stepper__step">
                            <div class="stepper__details">
                                <div class="timeline timeline_first">
                                    <div class="timeline__number">
                                        <font-awesome-icon icon="fa-solid fa-triangle-exclamation" size="m" />
                                    </div>
                                </div>
                                <div class="stepper__text stepper__text_first">
                                    <div class="stepper__header">{{ $gettext("Blocked") }}</div>
                                    <div class="stepper__subheader">
                                        {{ $gettext("Mollie seems to have blocked this account. Please unblock your account to use it further.") }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import settingsPageSetup from "@/mixins/settingsPageSetup";
import MolliePaymentsLeft from "@/components/MolliePaymentsLeft";
import MolliePaymentService from "@/store/services/molliepayment.service";
import CompanySettingService from "@/store/services/companysetting.service";
import GekkoButton from "@/components/GekkoButton";

export default {
    name: "MolliePayments",
    components: { MolliePaymentsLeft, GekkoButton },
    mixins: [settingsPageSetup],
    data() {
        return {
            pageSettings: {
                title: this.$gettext("iDEAL Payments"),
                showCompanyPicker: true
            },
            state: "",
            attach_mollie_payment: false,
            mollie: {},
            allProfiles: [],
            chosen_profile: null,
        }
    },
    computed: {
        isActiveState() {
            return this.state === 'verified' || this.state === 'in-review'
        }
    },
    created() {
        var vm = this;
        vm.disconnectMessage = this.$gettext('Are you sure you want to disconnect Mollie? If you do this, you will not be able to send iDEAL payments with your invoices.');
        vm.topMessage = this.$gettext('You can send iDEAL payment links with your invoices if you integrate Mollie.Check out more about Mollie on our')
        this.initialize();
    },
    methods: {
        initialize() {
            var vm = this;
            vm.state = "create";
            MolliePaymentService.allProfiles().then(function (response) {
                vm.allProfiles = response.data;
            });
            MolliePaymentService.profile().then(function (response) {
                vm.mollieUser = response.data;
                vm.handleProfileData(response.status);
            });
            CompanySettingService.list({ company: window.company_id }).then(function (response) {
                vm.companySettings = response.data[0];
                vm.attach_mollie_payment = response.data[0].attach_mollie_payment;
            });
        },
        handleProfileData(status) {
            let vm = this;
            vm.chosen_profile = vm.mollieUser.id;
            if (status === 298) {
                return;
            }
            MolliePaymentService.getOnboardingStatus().then(function (response) {
                vm.onboardingStatus = response.data;
                vm.state = vm.onboardingStatus.status;
                if (vm.state === 'completed') {
                    vm.state = vm.mollieUser.status;
                }
            });
        },
        connectMollie() {
            let vm = this;
            vm.spinner = true;
            vm.mollie.company_profile = window.company_id;
            MolliePaymentService.connectMollie(vm.mollie).then(function (response) {
                vm.spinner = false;
                try {
                    vm.$mixpanel.track('Mollie: connecting steps', { 'step': 'click connect', 'source': 'settings' },
                        function () {
                            window.location.href = response.data;
                        });
                } catch (err) {
                    window.location.href = response.data;
                }
            })
        },
        disconnect() {
            let vm = this;
            MolliePaymentService.disconnect().then(function () {
                vm.initialize();
            });
        },
        updateMollieOnCompanySettings() {
            let vm = this;
            if (vm.state === 'verified' || vm.state === 'in-review') {
                CompanySettingService.partialUpdate(vm.companySettings.id, {
                    "attach_mollie_payment": vm.attach_mollie_payment
                });
            } else {
                vm.attach_mollie_payment = false;
                this.$store.commit('setAlert', {
                    title: this.$gettext("Something went wrong when trying to connect!"),
                    text: this.$gettext("The connection to Mollie was unsuccessful. Please try again later."),
                    variant: "danger",
                    duration: 4
                });
            }
        },
        changeProfile() {
            let vm = this;
            MolliePaymentService.updateProfile({ profile_id: vm.chosen_profile }).then(function (response) {
                vm.mollieUser = response.data;
                vm.handleProfileData(response.status);
            })
        }
    }
}
</script>

<style lang="scss" scoped>
img {
    max-width: 50%;
    height: auto;
}

p {
    white-space: pre-line;
    max-width: 90%;
}

.btn {
    white-space: nowrap;
    min-width: auto;
}

.fa-check {
    padding-right: 2.5rem;
}

.process-box__container {
    margin-top: 2rem;
    white-space: pre-line;
    max-width: 70%;

    .process-box {
        border-radius: 0.5rem;
        position: relative;
        border: 1px solid $colorLight;
    }

    .process-box__header {
        min-height: 1rem;
        display: flex;
        align-items: center;
        padding: 0.5rem 0.5rem;
        position: relative;
        line-height: 1;
        border-bottom: 1px solid $colorLight;
        flex-wrap: wrap;
        background-color: $colorBrand50;
    }

    .process-box__logo {
        max-width: 9rem;
        min-width: 5rem;
        padding: 0.25rem;
        display: inline-block;
    }

    .process-box__title {
        padding: 0 0.2rem 0 0.5rem;
        flex-shrink: 0;

        @media(max-width: 414px) {
            order: 2;
            margin-left: auto;
            width: 100%;
            padding: 1rem 0 0.5rem 0.2rem;
        }
    }

    .process-box__status {
        margin-left: auto;
        padding-right: 0.75rem;
        font-family: DINNextLTPro-Medium, sans-serif;
        font-size: 1.25rem;
        color: $colorDanger;
    }

    .process-box__status_inactive {
        color: $colorWarning;
    }

    .process-box__status_active {
        color: $colorBrand;
    }

    .stepper {
        display: flex;
        flex-flow: column nowrap;
    }

    .stepper__step {
        width: 100%;
        flex: 1 1 auto;
        display: flex;
    }

    .stepper__step--grayedout {
        color: $colorUnassigned;
    }

    .stepper__action {
        flex: 0 1 0;
        display: flex;
        align-items: flex-start;
        justify-content: center;
        padding-top: 0.20rem;
        padding-right: 0.25rem;
        margin-left: auto;

        .btn {
            margin: 0.4rem 0.4rem;
        }
    }

    .stepper__action_first {
        padding-top: 0.6rem;
    }

    .stepper__details {
        flex: 1 1 auto;
        display: flex;
    }

    .stepper__text {
        padding: 0.4rem 0.2rem 0.75rem 0.5rem;
    }

    .stepper__text_first {
        padding-top: 0.8rem;
        padding-bottom: 1.75rem;
    }

    .stepper__header {
        font-size: 1.25rem;
        font-family: DINNextLTPro-Medium, sans-serif;
    }

    .stepper__subheader {
        font-size: 1rem;
        color: $colorTextSemi;
        margin-top: -0.1rem;
    }

    .timeline {
        min-width: 3rem;
        max-width: 3rem;
        display: flex;
        flex-flow: column nowrap;
        position: relative;
        align-items: center;
        padding-top: 0.3rem;
    }

    .timeline_first {
        padding-top: 1rem;
    }

    .timeline__symbol {
        position: relative;
        border: 2px solid $colorLight;
        border-radius: 100%;
        min-height: 2rem;
        max-height: 2rem;
        max-width: 2rem;
        min-width: 2rem;
        margin-bottom: 1px;
    }

    .timeline__number {
        display: block;
        position: absolute;
        top: 50%;
        left: 50%;
        line-height: 1;
        transform: translate(-50%, -50%);
        margin: 2px 0;
    }

    .timeline__vline {
        border-left: solid 1px $colorTextBase;
        flex: 1 0 auto;
        margin-top: 0.2rem;
        width: 1px;
    }

    .stepper__done {
        color: $colorBrand800;
        font-family: FontAwesome;
        font-size: 2rem;
    }

    .process-box__info {
        padding: 0.75rem 1rem 0.6rem 1rem;
        display: flex;
        flex-flow: column wrap;
    }

    .process-box__label {
        font-family: DINNextLTPro-Medium, sans-serif;
        color: $colorTextSemi;
        font-size: 1rem;
        padding-right: 0.6rem;
        margin: 0;
    }

    .process-box__value {
        color: $colorTextBase;
        font-size: 1rem;
        display: inline-block;
    }

    button.process-box__disconnect-btn {
        font-size: 1rem;
        color: $colorDanger;
    }

    .process-box__account-line,
    .process-box__status-line,
    .process-box__attach-line {
        padding-top: 0.4rem;
        display: inline-block;
    }

    .fa-ellipsis-vertical {
        color: $colorUnassigned;
    }
}

@media(max-width: 840px) {
    .process-box__container {
        max-width: 100%;
    }

    .process-box__content {
        .stepper__step {
            position: relative;
        }

        .stepper__action {
            position: absolute;
            bottom: 0;
            left: 3.3rem;
        }

        .stepper__text {
            padding-bottom: 3.75rem;
        }

        .stepper__step--grayedout {
            .stepper__text {
                padding-bottom: 2rem;
            }

            .stepper__action {
                left: 3.5rem;
            }
        }
    }
}
</style>